/**
 * Pure helpers shared by the file-translation dialog, its service and its subscription hook.
 *
 * Every function here must stay in sync with the Lambda layer:
 *   - normalizeLangCode        <-> Translation.normalizeLangCode
 *   - buildTranslatedFileName  <-> FileTranslationCoreService.buildTranslatedFileName
 *   - mergeJobsIntoLangStatusMap <-> AttachmentTranslationService.mergeJobsIntoLangStatusMap
 *   - hashS3Key                <-> TranslationJobService.hashS3Url
 */

import CryptoJS from 'crypto-js';


export const TRANSLATION_TARGET_STATUS = {
    PENDING: 'PENDING',
    RUNNING: 'RUNNING',
    SUCCEEDED: 'SUCCEEDED',
    FAILED: 'FAILED'
};

export const TRANSLATION_SOURCE_LANG_AUTO = 'auto';

export const TRANSLATION_MAX_TARGET_LANGS = 10;

export const TRANSLATION_SUPPORTED_EXTENSIONS = ['pdf', 'docx', 'pptx', 'xlsx'];

export const TRANSLATION_INLINE_VIEW_EXTENSIONS = ['pdf'];

// Mirrors Translation.TRANSLATION_LANG_ALIAS in the Lambda layer: two codes Google accepts for one
// language, normalised to the one TranslationConstant.TRANSLATION_LANG_DISPLAY_JP holds.
const TRANSLATION_LANG_ALIAS = {
    he: 'iw',
    tl: 'fil'
};

// A language that already SUCCEEDED must never be hidden by a newer FAILED row: the translated
// file still exists on the server, and the no-overwrite rule would otherwise leave the user
// unable to open it AND unable to translate it again.
const STATUS_PRIORITY = {
    [TRANSLATION_TARGET_STATUS.SUCCEEDED]: 3,
    [TRANSLATION_TARGET_STATUS.RUNNING]: 2,
    [TRANSLATION_TARGET_STATUS.PENDING]: 2,
    [TRANSLATION_TARGET_STATUS.FAILED]: 1
};

const priorityOf = status => STATUS_PRIORITY[status] || 0;

export const normalizeLangCode = code => {
    if (!code || typeof code !== 'string') return code;
    const trimmed = code.trim();
    return TRANSLATION_LANG_ALIAS[trimmed] || trimmed;
};

export const getFileExtension = fileName => {
    if (!fileName || typeof fileName !== 'string') return '';
    const idx = fileName.lastIndexOf('.');
    if (idx === -1) return '';
    return fileName.slice(idx + 1).toLowerCase();
};

export const isTranslatableFileName = fileName =>
    TRANSLATION_SUPPORTED_EXTENSIONS.includes(getFileExtension(fileName));

// Only a PDF is rendered by the browser; the Office formats are downloaded.
export const isInlineViewableFileName = fileName => TRANSLATION_INLINE_VIEW_EXTENSIONS.includes(getFileExtension(fileName));

/**
 * Inserts `_{langCode}` before the LAST dot so a name with several dots keeps them:
 *   bao.cao.v2.pdf + en -> bao.cao.v2_en.pdf
 */
export const buildTranslatedFileName = (sourceFileName, langCode) => {
    if (!sourceFileName) return `translated_${langCode}`;

    const lastDotIndex = sourceFileName.lastIndexOf('.');
    if (lastDotIndex === -1) return `${sourceFileName}_${langCode}`;

    return `${sourceFileName.substring(0, lastDotIndex)}_${langCode}.${sourceFileName.substring(lastDotIndex + 1)}`;
};

/**
 * S3 keys are stored on the job with the `public/` prefix, while the attachment field keeps them
 * without it. Everything that hashes or compares a key goes through this first.
 */
export const normalizeS3Key = fileUrl => {
    if (!fileUrl) return '';
    return fileUrl.startsWith('public/') ? fileUrl : `public/${fileUrl}`;
};

/**
 * Must match TranslationJobService.hashS3Url in the Lambda layer bit for bit:
 *   crypto.createHash('sha256').update(String(s3Url)).digest('hex')
 * The input has to be a key that already went through normalizeS3Key - the backend hashes the
 * prefixed form, so hashing the raw key finds nothing.
 */
export const hashS3Key = s3Key => {
    if (!s3Key) return '';
    return CryptoJS.SHA256(String(s3Key)).toString(CryptoJS.enc.Hex);
};

/**
 * Whether a job, invalidated or not, was started for the file the dialog is showing.
 * onTranslationJobUpdated fires for every job in the organization - AI Chat translations and other
 * files included - so this filter is what keeps the dialog from reacting to strangers.
 *
 * @param {string} [requestSource] only accept jobs started from this entry point
 */
export const isJobOfSourceFile = (job, sourceS3Url, affiliationOrganization, requestSource) => {
    if (!job) return false;
    if (affiliationOrganization && job.affiliationOrganization !== affiliationOrganization) return false;
    if (requestSource && job.requestSource !== requestSource) return false;
    return normalizeS3Key(job.sourceS3Url) === normalizeS3Key(sourceS3Url);
};

/**
 * Same as isJobOfSourceFile, restricted to jobs that still count (source file not replaced/deleted).
 */
export const isJobForSource = (job, sourceS3Url, affiliationOrganization, requestSource) =>
    isJobOfSourceFile(job, sourceS3Url, affiliationOrganization, requestSource) && !job.invalidatedAt;

/**
 * Drops every language whose entry came from the given job - used when that job gets invalidated
 * while the dialog is open (the source file was replaced or deleted).
 */
export const removeJobFromLangStatusMap = (langStatusMap, jobId) => {
    if (!jobId) return langStatusMap || {};

    return Object.keys(langStatusMap || {}).reduce((acc, lang) => {
        if (langStatusMap[lang]?.jobId !== jobId) acc[lang] = langStatusMap[lang];
        return acc;
    }, {});
};

const parseJobResults = job => {
    if (!job?.results) return {};
    if (typeof job.results !== 'string') return job.results;

    try {
        return JSON.parse(job.results) || {};
    } catch (error) {
        return {};
    }
};

/**
 * Collapses every translation job of one source file into { [langCode]: entry }.
 *
 * One file accumulates several jobs: each translate click creates a new one, a retry after a
 * failure adds another, and the 12h timeout sweeper can fail an old one. Picking "the newest job"
 * alone would therefore hide a translation that is still on the server.
 *
 * @param {object[]} jobs
 * @param {string} [affiliationOrganization] defence-in-depth filter
 * @returns {Object.<string, {status, jobId, fileName, s3Url, errorMessage, translatedAt, createdAt}>}
 */
export const mergeJobsIntoLangStatusMap = (jobs, affiliationOrganization) => {
    let map = {};

    (jobs || []).forEach(job => {
        if (!job || job.invalidatedAt) return;
        if (affiliationOrganization && job.affiliationOrganization !== affiliationOrganization) return;

        const results = parseJobResults(job);
        const incoming = {};

        (job.targetLangs || []).forEach(rawLang => {
            const lang = normalizeLangCode(rawLang);
            const result = results[rawLang] || results[lang] || {};

            incoming[lang] = {
                status: result.status || TRANSLATION_TARGET_STATUS.PENDING,
                jobId: job.id,
                fileName: buildTranslatedFileName(job.sourceFileName, lang),
                s3Url: result.s3Url || null,
                errorMessage: result.errorMessage || null,
                translatedAt: result.translatedAt || null,
                createdAt: job.createdAt
            };
        });

        map = mergeLangStatusMaps(map, incoming);
    });

    return map;
};

/**
 * Folds one map of per-language entries into another. Initial load, subscription payloads and the
 * optimistic seed all go through here, so the ordering rules live in exactly one place.
 *
 * A seeded entry always loses to a real one regardless of timestamps: the seed carries a client
 * clock and the job a server clock, and since PENDING and RUNNING share a priority, a clock skew of
 * a few seconds would otherwise reject the real payload and leave the row stuck at 翻訳中 forever.
 */
export const mergeLangStatusMaps = (currentMap, incomingMap) => {
    const merged = { ...(currentMap || {}) };

    Object.keys(incomingMap || {}).forEach(lang => {
        const incoming = incomingMap[lang];
        const current = merged[lang];

        if (!current) {
            merged[lang] = incoming;
            return;
        }

        if (current.isOptimistic && !incoming.isOptimistic) {
            merged[lang] = incoming;
            return;
        }

        const isHigherPriority = priorityOf(incoming.status) > priorityOf(current.status);
        const isSamePriorityButNewer =
            priorityOf(incoming.status) === priorityOf(current.status) &&
            `${incoming.createdAt}` > `${current.createdAt}`;

        if (isHigherPriority || isSamePriorityButNewer) merged[lang] = incoming;
    });

    return merged;
};

/**
 * Fills the gap between the translate call returning and the first subscription event.
 *
 * onTranslationJobUpdated only fires on updateTranslationJob, never on create, so nothing would
 * appear in the status list until translateFileHandler moves the job to RUNNING - seconds later.
 * Without this the user presses 翻訳開始, sees nothing happen, presses again, and the server
 * answers IN_PROGRESS.
 */
export const buildOptimisticLangStatusMap = ({ accepted, sourceFileName, jobId }) => {
    const createdAt = new Date().toISOString();

    return (accepted || []).reduce((acc, rawLang) => {
        const lang = normalizeLangCode(rawLang);
        acc[lang] = {
            status: TRANSLATION_TARGET_STATUS.PENDING,
            jobId,
            fileName: buildTranslatedFileName(sourceFileName, lang),
            s3Url: null,
            errorMessage: null,
            translatedAt: null,
            createdAt,
            isOptimistic: true
        };
        return acc;
    }, {});
};

/**
 * Languages that must not be offered again: a translation already exists or is on its way.
 * A FAILED language stays selectable so the user can retry it.
 */
export const isLangLockedByStatus = entry =>
    Boolean(entry) &&
    [
        TRANSLATION_TARGET_STATUS.SUCCEEDED,
        TRANSLATION_TARGET_STATUS.RUNNING,
        TRANSLATION_TARGET_STATUS.PENDING
    ].includes(entry.status);

/**
 * Rows of the translation-status section, ordered oldest first so a running translation does not
 * jump around the list while the user is looking at it.
 */
export const buildStatusRows = langStatusMap => {
    return Object.keys(langStatusMap || {})
        .map(lang => ({ lang, ...langStatusMap[lang] }))
        .sort((a, b) => `${a.createdAt}`.localeCompare(`${b.createdAt}`) || a.lang.localeCompare(b.lang));
};

const translationJobUtils = {
    TRANSLATION_TARGET_STATUS,
    TRANSLATION_SOURCE_LANG_AUTO,
    TRANSLATION_MAX_TARGET_LANGS,
    TRANSLATION_SUPPORTED_EXTENSIONS,
    normalizeLangCode,
    getFileExtension,
    isTranslatableFileName,
    isInlineViewableFileName,
    buildTranslatedFileName,
    normalizeS3Key,
    hashS3Key,
    isJobOfSourceFile,
    isJobForSource,
    removeJobFromLangStatusMap,
    mergeJobsIntoLangStatusMap,
    mergeLangStatusMaps,
    buildOptimisticLangStatusMap,
    isLangLockedByStatus,
    buildStatusRows
};

export default translationJobUtils;
